import { getFreshTokensAndClaims, useMSALPayload } from "@initia-digital/auth-client-utils";
import { UIWorkAndSafetyService } from "@initia-digital/data-sdk";
import { BasicLogger } from "@initia-digital/ts-utils";
import { CredentialInput } from "@initia-digital/types/service";
import { IPlan, IPlanReviewNote, IPrestart } from "@initia-digital/types/work-and-safety";
import { wrapRefreshCognitoCredentialsUtil } from "./auth";
import { CONFIG } from "../config/const"

const { CDN_BASE_URL } = CONFIG;

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' })


// --------------------------------------------
//           WORK AND SAFETY SERVICE
// --------------------------------------------
const wsService = new UIWorkAndSafetyService({
    env: import.meta.env.VITE_ENV_LOWER,
    credentialProviderProps: {
      authProvider: import.meta.env.VITE_AUTH_PROVIDER,
      region: 'ap-southeast-2',
      identityPoolId: import.meta.env.VITE_AUTH_COGNITO_IDENTITY_POOL_ID,
    },
    logger
  });

export const postPlan = async ({
    plan, authDetail
  }:
  {
    plan: IPlan,
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);
  
    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();
    const response = await apiClient.request<IPlan>(
      credentialsInput,
      {
        method: 'POST',
        templatePath: '/plan',
        body: plan,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data;
  }


  export const putPlan = async ({
    planId, plan, authDetail
  }: {
    planId: string,
    plan: IPlan,
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);
  
    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();

    const response = await apiClient.request<IPlan>(
      credentialsInput,
      {
        method: 'PUT',
        templatePath: '/plan/:id',
        pathParameters: {
          id: planId
        },
        body: plan,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data;
  }


  export const patchPlan = async ({
    planId, notify, patches, authDetail
  }:
  {
    planId: string,
    notify: boolean,
    patches: {
      op: "add" | "remove" | "replace" | "move" | "copy" | "test"
      path: string
      value?: unknown
      from?: string
    }[],
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();
    const response = await apiClient.request(
      credentialsInput,
      {
        method: 'PATCH',
        templatePath: '/plan/:id',
        pathParameters: {
          id: planId
        },
        queryStringParameters: {
          notify: notify ? 'true' : 'false'
        },
        body: patches,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data as IPlan;
  }


  export const postPrestart = async ({
    prestart, authDetail
  }:
  {
    prestart: IPrestart,
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);
  
    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();
    const response = await apiClient.request<IPrestart>(
      credentialsInput,
      {
        method: 'POST',
        templatePath: '/prestart',
        body: prestart,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data;
  }

  export const putPrestart = async ({
    prestartId, prestart, authDetail
  }: {
    prestartId: string,
    prestart: IPrestart,
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);
  
    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();

    const response = await apiClient.request<IPrestart>(
      credentialsInput,
      {
        method: 'PUT',
        templatePath: '/prestart/:id',
        pathParameters: {
          id: prestartId
        },
        body: prestart,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data;
  }

  export const patchPrestart = async ({
    prestartId, notify, patches, authDetail
  }:
  {
    prestartId: string,
    notify: boolean,
    patches: {
      op: "add" | "remove" | "replace" | "move" | "copy" | "test"
      path: string
      value?: unknown
      from?: string
    }[],
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();
    const response = await apiClient.request(
      credentialsInput,
      {
        method: 'PATCH',
        templatePath: '/prestart/:id',
        pathParameters: {
          id: prestartId
        },
        queryStringParameters: {
          notify: notify ? 'true' : 'false'
        },
        body: patches,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )
    return response.data as IPrestart;
  }


  export const postPlanReviewComment = async ({
    jobId, planId, reviewComment, authDetail
  }: {
    jobId: string,
    planId: string,
    reviewComment: string,
    authDetail: Awaited<ReturnType<typeof useMSALPayload>>
  }) => {
    // --------------------------------------------
    const { msal } = authDetail;
    const { idToken, idTokenClaims, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

    const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

    const credentialsInput = {
      type: "CognitoIdentity",
      idToken: idToken,
      service: 'execute-api'
    } as CredentialInput;

    const apiClient = wsService.getApiClient();
    const response = await apiClient.request<IPlanReviewNote>(
      credentialsInput,
      {
        method: 'POST',
        templatePath: '/job/:jobId/plan/:planId/reviewNote',
        pathParameters: {
          jobId,
          planId
        },
        body: {
          reviewedBy: 'name' in idTokenClaims && typeof idTokenClaims.name === 'string' ? idTokenClaims.name : '',
          reviewedByEmail: 'preferred_username' in idTokenClaims && typeof idTokenClaims.preferred_username === 'string' ? idTokenClaims.preferred_username.toLowerCase() : '',
          reviewedByIcon: 'oid' in idTokenClaims && typeof idTokenClaims.oid === 'string' ? `${CDN_BASE_URL}/img/staff-media/${idTokenClaims.oid}.jpg` : `${CDN_BASE_URL}/staff-media/default.jpg`,
          notes: reviewComment
        },
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_HS_API_KEY,
          'id-token': idToken,
          'bearer-token': accessToken
        }
      },
      wrapRefreshCredentials
    )

    return response.data;
  }


export const deletePlanReviewComment = async ({
  jobId, planId, reviewNoteId, authDetail
}: {
  jobId: string,
  planId: string,
  reviewNoteId: string,
  authDetail: Awaited<ReturnType<typeof useMSALPayload>>
}) => {
  // --------------------------------------------
  const { msal } = authDetail;
  const { idToken, accessToken } = await getFreshTokensAndClaims(msal, { scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"] });

  const wrapRefreshCredentials = wrapRefreshCognitoCredentialsUtil(authDetail);

  const credentialsInput = {
    type: "CognitoIdentity",
    idToken: idToken,
    service: 'execute-api'
  } as CredentialInput;

  const apiClient = wsService.getApiClient();
  const response = await apiClient.request(
    credentialsInput,
    {
      method: 'DELETE',
      templatePath: '/job/:jobId/plan/:planId/reviewNote/:reviewNoteId',
      pathParameters: {
        jobId,
        planId,
        reviewNoteId
      },
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_HS_API_KEY,
        'id-token': idToken,
        'bearer-token': accessToken
      }
    },
    wrapRefreshCredentials
  )

  return response.data;
}